import { useEffect, useRef, useState, useCallback } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { io, Socket } from 'socket.io-client'
import { Toaster, toast } from '@/components/ui/toaster'
import SharePage from '@/pages/SharePage'
import LoginPage from '@/pages/LoginPage'
import DisplayPage from '@/pages/DisplayPage'
import DisplayNotificationPage from '@/pages/DisplayNotificationPage'
import TimelinePage from '@/pages/TimelinePage'
import ArchivePage from '@/pages/ArchivePage'
import AdminPage from '@/pages/AdminPage'
import GlobalSettingsPage from '@/pages/GlobalSettingsPage'
import ManageEntriesPage from '@/pages/ManageEntriesPage'
import ManageUsersPage from '@/pages/ManageUsersPage'
import ManageGalleriesPage from '@/pages/ManageGalleriesPage'
import PushPage from '@/pages/PushPage'
import DownloadPage from '@/pages/DownloadPage'
import MyPingsPage from '@/pages/MyPingsPage'
import PostDetailPage from '@/pages/PostDetailPage'
import StatsPage from '@/pages/StatsPage'
import { UpdateModal } from '@/components/UpdateModal'
import { isCapacitorNative, checkShareIntent, getApiBaseUrl } from '@/lib/capacitor'
import { useUpdateCheck } from '@/hooks/useUpdateCheck'

// Helper to get user ID from localStorage
function getUserIdFromStorage(): string | null {
  const userJson = localStorage.getItem('ping_user') || localStorage.getItem('ping_local_user');
  if (!userJson) return null;
  
  try {
    const user = JSON.parse(userJson);
    return user.id || user._id || null;
  } catch {
    return null;
  }
}

function App() {
  const location = useLocation()
  const navigate = useNavigate()
  const socketRef = useRef<Socket | null>(null)
  const currentUserIdRef = useRef<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  
  // Check for app updates (only on native platforms)
  const {
    isUpdateModalOpen,
    latestVersion,
    currentVersion,
    installState,
    dismissUpdate,
    startUpdateInstall,
  } = useUpdateCheck()

  // Poll for user ID changes (handles login/logout and initial load timing)
  useEffect(() => {
    // Check immediately
    const initialUserId = getUserIdFromStorage();
    if (initialUserId) {
      setUserId(initialUserId);
    }

    // Poll every 500ms for the first 5 seconds to catch delayed localStorage updates
    // Then poll every 2 seconds to catch login/logout
    let pollCount = 0;
    const poll = () => {
      const newUserId = getUserIdFromStorage();
      if (newUserId !== userId) {
        setUserId(newUserId);
      }
      pollCount++;
    };

    // Fast polling initially
    const fastInterval = setInterval(() => {
      poll();
      if (pollCount >= 10) {
        clearInterval(fastInterval);
      }
    }, 500);

    // Slow polling for ongoing login/logout detection
    const slowInterval = setInterval(poll, 2000);

    return () => {
      clearInterval(fastInterval);
      clearInterval(slowInterval);
    };
  }, [userId]);

  // Connect socket when user ID changes
  const connectSocket = useCallback((newUserId: string) => {
    // Don't reconnect if already connected with same user
    if (socketRef.current && currentUserIdRef.current === newUserId) {
      return;
    }

    // Disconnect existing socket if any
    if (socketRef.current) {
      console.log('[App] Disconnecting existing socket for user:', currentUserIdRef.current);
      socketRef.current.emit('leave-user-room', currentUserIdRef.current);
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Connect to socket server
    const baseUrl = getApiBaseUrl();
    console.log('[App] Connecting socket to:', baseUrl, 'for user:', newUserId);
    
    const socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;
    currentUserIdRef.current = newUserId;

    socket.on('connect', () => {
      console.log('[App] Socket connected:', socket.id);
      // Join user-specific room for targeted notifications
      socket.emit('join-user-room', newUserId);
    });

    socket.on('connect_error', (error) => {
      console.error('[App] Socket connection error:', error.message);
    });

    socket.on('disconnect', (reason) => {
      console.log('[App] Socket disconnected:', reason);
    });

    // Listen for video processing progress events
    socket.on('processing-progress', (data: { postId: string; percent: number; frames?: number; fps?: number; time?: string }) => {
      // Dispatch a custom event so pages can update their progress UI
      window.dispatchEvent(new CustomEvent('ping-processing-progress', { 
        detail: data 
      }));
    });

    // Listen for video processing complete events
    socket.on('processing-complete', (data: { postId: string; success: boolean }) => {
      console.log('[App] Processing complete event:', data);
      
      // Dispatch a custom event so pages can listen and refresh their data
      window.dispatchEvent(new CustomEvent('ping-processing-complete', { 
        detail: { postId: data.postId, success: data.success } 
      }));
      
      if (data.success) {
        toast({
          title: 'Video Ready! 🎬',
          description: 'Your video has finished processing and is now live.',
          duration: 8000,
        });
      } else {
        toast({
          title: 'Processing Failed',
          description: 'There was a problem processing your video.',
          variant: 'destructive',
          duration: 10000,
        });
      }
    });
  }, []);

  // Socket connection management based on userId state
  useEffect(() => {
    if (userId) {
      connectSocket(userId);
    } else if (socketRef.current) {
      // User logged out, disconnect socket
      console.log('[App] User logged out, disconnecting socket');
      socketRef.current.emit('leave-user-room', currentUserIdRef.current);
      socketRef.current.disconnect();
      socketRef.current = null;
      currentUserIdRef.current = null;
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave-user-room', currentUserIdRef.current);
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [userId, connectSocket]);

  // App-level share intent detection
  // When a share intent is received from another app, navigate to /share
  useEffect(() => {
    if (!isCapacitorNative()) {
      return;
    }

    const handleShareIntent = async () => {
      console.log('[App] sendIntentReceived event - navigating to /share');
      // Navigate to share page to handle the intent
      navigate('/share');
    };

    // Check for share intent on initial load (cold start from share)
    const checkInitialIntent = async () => {
      try {
        const intent = await checkShareIntent();
        if (intent && location.pathname !== '/share') {
          console.log('[App] Initial share intent detected, navigating to /share');
          navigate('/share');
        }
      } catch (err) {
        // Ignore errors - no intent present
      }
    };

    checkInitialIntent();

    // Listen for sendIntentReceived event (when app receives share while running)
    window.addEventListener('sendIntentReceived', handleShareIntent);

    return () => {
      window.removeEventListener('sendIntentReceived', handleShareIntent);
    };
  }, [navigate, location.pathname]);
  
  // On Capacitor native, redirect root to /share (the mobile app is for sharing)
  if (isCapacitorNative() && location.pathname === '/') {
    return <Navigate to="/share" replace />
  }

  return (
    <>
      <Routes>
        <Route path="/display" element={<DisplayPage />} />
        <Route path="/display/notification" element={<DisplayNotificationPage />} />
        <Route path="/share" element={<SharePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/timeline" element={<TimelinePage />} />
        <Route path="/archive" element={<ArchivePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/settings" element={<GlobalSettingsPage />} />
        <Route path="/admin/entries" element={<ManageEntriesPage />} />
        <Route path="/admin/users" element={<ManageUsersPage />} />
        <Route path="/admin/galleries" element={<ManageGalleriesPage />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/push" element={<PushPage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/my-pings" element={<MyPingsPage />} />
        <Route path="/my-pings/:postId" element={<PostDetailPage />} />
      </Routes>
      <Toaster />
      
      {/* App update modal - only shown on native when update is available */}
      <UpdateModal
        open={isUpdateModalOpen}
        latestVersion={latestVersion}
        currentVersion={currentVersion}
        installState={installState}
        onDismiss={dismissUpdate}
        onPrimaryAction={startUpdateInstall}
      />
    </>
  )
}

export default App
